import * as React from 'react';
import { BuilderElement } from '@builder.io/react';
import classNames from 'classnames';
import DefaultImage from '@/assets/images/defaultImage.jpg';
import { Image } from '@/components/shared';
import { useDamFile } from '@/contexts/DamFileProvider';
import { useIsHighPriorityBlock } from '@/contexts/HighPriorityBlocksProvider';
import { MediaSet } from '@/types/media';
import styles from './DamImage.module.scss';
import { DamImageComponentProps } from './register';

const fallBackImage: MediaSet = {
	size180: { url: DefaultImage },
	size320: { url: DefaultImage },
	size480: { url: DefaultImage },
	size720: { url: DefaultImage },
	size960: { url: DefaultImage },
	size1280: { url: DefaultImage },
	size1920: { url: DefaultImage },
};

interface DamImageProps extends DamImageComponentProps {
	readonly builderBlock: BuilderElement;
}

/**
 * CMS Image component, used for creating Image. It overrides Builder's default
 * image component, but source uses DAM images.
 * */
export const DamImage: React.FunctionComponent<DamImageProps> = ({
	children,
	image,
	altText,
	imageFit,
	imagePosition,
	aspectRatio,
	attributes,
	builderBlock,
}) => {
	const damImage = useDamFile(image ?? null) ?? fallBackImage;
	const highPriority = useIsHighPriorityBlock(builderBlock.id ?? null);

	const { className: builderClassNames, ...restOfAttributes } = attributes || {};

	return (
		<div
			className={classNames(
				styles.wrapper,
				{
					[styles.hasChildren]: children,
					[styles.hasTabletAspectRatio]: typeof aspectRatio?.tablet === 'number',
					[styles.hasMobileAspectRatio]: typeof aspectRatio?.mobile === 'number',
				},
				builderClassNames,
			)}
			{...restOfAttributes}
			key={'damImage'}
			style={
				{
					'--imageFit': imageFit ? imageFit : 'cover',
					'--imagePosX': imagePosition?.x ? `${imagePosition.x}%` : 'center',
					'--imagePosY': imagePosition?.y ? `${imagePosition.y}%` : 'center',
					'--desktopAspectRatio': aspectRatio?.desktop ?? 0,
					'--tabletAspectRatio': aspectRatio?.tablet ?? undefined,
					'--mobileAspectRatio': aspectRatio?.mobile ?? undefined,
				} as React.CSSProperties
			}
		>
			{damImage ? (
				<Image className={styles.image} {...damImage} altText={altText ?? damImage.altText ?? ''} lazyLoading={!highPriority} />
			) : null}
			{children}
		</div>
	);
};
